// COP — dual classification banner (the signature furniture).
//
// "The data carries its own releasability" made visible: a DoD/IC-compliant
// classification BANNER LINE at BOTH the top and bottom of the screen. Per
// DoD/IC marking standards (DoDM 5200.01 Vol 2 / ISOO / the CAPCO Authorized
// Classification and Control Markings Register), the banner line is ONE
// properly-ordered marking string showing the OVERALL (highest / high-water)
// classification of the displayed content, and it appears IDENTICALLY at the
// top and bottom — not top=marking / bottom=releasability.
//
// The marking is derived from the active entities' security labels
// (CONTRACT:I2-SECURITY-LABELS.1.0) via the high-water aggregate — not hardcoded.
// The line itself is built by the pure, standards-citing builder in dod-marking.ts.
// Background colors follow standard US marking colors.

import type { HighWater } from "../host/cop.ts";
import {
  bannerLine,
  bannerColor,
  bannerTextColor,
  bannerTier,
  capcoCaption,
  CAPCO_LINE_LABEL,
} from "./dod-marking.ts";

export function ClassificationBanner({
  highWater,
  position,
}: {
  highWater: HighWater;
  position: "top" | "bottom";
}): JSX.Element {
  // Color TIER elevates TOP SECRET + SCI compartment to the gold TS//SCI banner
  // (display-only; the marking string is unchanged). Foreground: near-black on gold,
  // white elsewhere (WCAG-legible on every CAP background; tak-design-system canon).
  const tier = bannerTier(highWater);
  const color = bannerColor(tier);
  const textColor = bannerTextColor(tier);
  // Identical compliant banner line at top AND bottom (DoD standard). This banner
  // reflects the AS-RENDERED overall marking (the DATA side), so it carries the
  // "Classification:" CAPCO caption — matching the dual-banner ROW 2 wording.
  const text = bannerLine(highWater);
  const caption = capcoCaption(CAPCO_LINE_LABEL.AS_RENDERED, text);
  return (
    <div
      className={`classbar classbar--${position}`}
      style={{ background: color, color: textColor }}
      role="status"
      aria-label={`${position} classification banner: ${caption}`}
    >
      <span className="classbar__cattag" aria-hidden="true">
        {CAPCO_LINE_LABEL.AS_RENDERED}:
      </span>
      <span className="classbar__text">{text}</span>
    </div>
  );
}
